import { Prompt } from 'jobs/prompts/Prompt'
import { action, computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { MessageContentViewModel } from '../../../MessageViewModel'
import Message from 'messaging/models/Message'
import { PromptContent } from 'messaging/models/Content'
import { Id } from 'types/definitions'

// Used for formatting the completion date.
const dateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'short' })
const timeFormatter = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' })

/**
 * Prompt content.
 */
export class PromptMessageContentViewModel implements MessageContentViewModel {
  constructor(
    public message: Message,
    private _fetchPrompt: (promptId: Id) => Promise<void>,
    private _viewPrompt: (prompt: Prompt) => void,
  ) {
    makeObservable(this)
  }

  /**
   * The prompt.
   */
  @computed
  get prompt() {
    const content = this.message.content as PromptContent
    return content.prompt
  }

  /**
   * Formatted completion time.
   */
  @computed
  get completionTimestampText() {
    const date = this.prompt.dateAnswered

    return date
      ? `${dateFormatter.format(date)} at ${timeFormatter.format(date)}`
      : ''
  }

  /**
   * Activates the view model.
   */
  async activate() {
    if (this.prompt.question) {
      // Don't start the fetch task if we already have the prompt info.
      // This prevents a scroll jump issue when loading the first page of messages
      return
    }
    await this.fetchPrompt(this.prompt.id)
  }

  /**
   * Fetches the prompt.
   */
  fetchPrompt = task.resolved(async (promptId: Id) => {
    await this._fetchPrompt(promptId)
  })

  /**
   * Requests to view the prompt.
   */
  @action.bound
  view() {
    this._viewPrompt(this.prompt)
  }
}
